"use client";

import { useLingui } from "@lingui/react/macro";
import type { CountryValue } from "@offstreetparking/geography";
import { useState } from "react";
import { offstreetClient } from "../../api/client";
import type { RouterOutputs } from "../../api/types";
import { useCompany } from "../../providers/company";
import { DeleteVehicleConfirmationModal } from "./DeleteVehicleConfirmationModal";
import { VehicleForm } from "./VehicleForm";
import { VehicleModal } from "./VehicleModal";
import { useVehicleSubmitError } from "./useVehicleSubmitError";
type ParkerVehicle = RouterOutputs["parkers"]["getParkerVehicles"]["items"][number];
type EditVehicleModalProps = {
	isOpen: boolean;
	vehicle?: ParkerVehicle;
	country?: CountryValue;
	onClose: () => void;
	onEdit: (payload: RouterOutputs["parkers"]["updateParkerVehicle"]) => Promise<void>;
	onRemove: (payload: RouterOutputs["parkers"]["removeParkerVehicle"]) => Promise<void>;
};
export const EditVehicleModal = ({
	isOpen,
	vehicle,
	country: countryProp,
	onEdit,
	onRemove,
	onClose,
}: EditVehicleModalProps) => {
	const { t } = useLingui();
	const { company } = useCompany();
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
	const updateVehicleMutation = offstreetClient.parkers.updateParkerVehicle.useMutation({
		onSuccess: onEdit,
	});
	const removeVehicleMutation = offstreetClient.parkers.removeParkerVehicle.useMutation({
		onSuccess: async (payload) => {
			await onRemove(payload);
			setIsConfirmingDelete(false);
		},
	});
	const submitError = useVehicleSubmitError(updateVehicleMutation.error ?? removeVehicleMutation.error);
	const country = countryProp ?? company?.country;
	if (!country) {
		return null;
	}
	const isDeleteModalOpen = isConfirmingDelete && !!vehicle;
	return (
		<>
			<VehicleModal isOpen={isOpen && !isDeleteModalOpen} onClose={onClose} title={t`Edit Vehicle`}>
				{!!vehicle && (
					<VehicleForm
						country={country}
						initialValues={{
							plate: vehicle.plate,
							state: vehicle.state,
							nickname: vehicle.nickname ?? "",
							isDefault: vehicle.isDefault,
						}}
						isDefaultHidden={vehicle.isDefault}
						isSubmitting={updateVehicleMutation.isPending}
						onCancel={() => onClose()}
						onSubmit={async (values) => {
							await updateVehicleMutation.mutateAsync({
								vehicleId: vehicle.id,
								plate: values.plate,
								state: values.state,
								nickname: values.nickname?.trim() || null,
								isDefault: values.isDefault,
							});
						}}
						onRemove={() => setIsConfirmingDelete(true)}
						removeIsPending={removeVehicleMutation.isPending}
						submitError={submitError}
						nicknameLabel={t`Nickname`}
						defaultVehicleLabel={t`Default Vehicle`}
						removeVehicleLabel={t`Remove Vehicle`}
						identityChangeWarningMessage={t`Changes won't affect existing permits. Update permits separately if needed.`}
					/>
				)}
			</VehicleModal>
			<DeleteVehicleConfirmationModal
				isOpen={isDeleteModalOpen}
				isLoading={removeVehicleMutation.isPending}
				plate={vehicle?.plate ?? ""}
				onClose={() => setIsConfirmingDelete(false)}
				onConfirm={() => {
					if (vehicle) {
						removeVehicleMutation.mutate({
							vehicleId: vehicle.id,
						});
					}
				}}
			/>
		</>
	);
};
